import React, {ComponentRef, useEffect, useMemo, useRef, useState} from 'react';
import {Platform, Text, TouchableOpacity, View} from 'react-native';
import {useIsZoomed} from '../../../../shared/hooks/useSiteHeaderHeight';
import FASTERLogoLight from '../../../../shared/assets/icons/FASTERLogoRGB';
import FASTERLogoDark from '../../../../shared/assets/icons/FASTERLogoRGBDark';
import DarkModeIcon from '../../../../shared/assets/icons/darkModeIcon';
import LightModeIcon from '../../../../shared/assets/icons/lightModeIcon';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {createHeaderStyles} from '../../../../shared/components/layout/Header/Header.styles';
import HelpIcon from '../../../../shared/assets/icons/helpIcon';
import {LogoutIcon} from '../../../../shared/assets/icons/LogoutIcon';
import {Button, Dropdown, SearchInput} from '../../../../shared/components/ui';
import LanguageIcon from '../../../../shared/assets/icons/languageIcon';
import {useCommonController} from '../../../../shared/controllers/commonController';
import {useDispatch, useSelector} from 'react-redux';
import {DropdownOption} from '../../../../shared/components/ui/Dropdown/DropdownProps';
import {RootState} from '../../../../shared/store';
import {setLanguage} from '../../../../shared/store/languageSlice';
import {setColorMode} from '../../../../shared/store/themeSlice';
import {
  navigate,
  navigationRef,
} from '../../../../shared/navigation/navigationRef';
import {intentionalNavFlag} from '../../../../shared/navigation/intentionalNavFlag';
import {
  HeaderProps,
  IndirectActivityResponse,
} from '../../../../shared/components/layout/Header/HeaderProps';
import {storageService} from '../../../../shared/services/storageService';
import {signoutWithBrowser} from '../../../../features/auth/services/authProvider';
import {clearSelectedTechnicianData} from '../../../../shared/store/technicianSlice';
import {closeQuickAssistScreen} from '../../../../shared/store/quickAssistSlice';
import secureStorage from '../../../../shared/services/secureStorage';
import {
  REPAIR_FLOW_PERSIST_STEP_KEY,
  REPAIR_FLOW_PERSIST_WORK_ORDER_KEY,
  WO_FLOW_PERSIST_STEP_KEY,
  WO_FLOW_PERSIST_ASSET_KEY,
  WO_FLOW_PERSIST_DOCUMENT_ID_KEY,
  WO_FLOW_PERSIST_STEP1_KEY,
} from '../../../../features/CreateFlow/CreateFlowConstant';
import {userIdStorage} from '../../../../features/auth/api/userIdStorage';
import IndirectActivityIcon from '../../../../shared/assets/icons/IndirectActivity';
import {menuTabs} from '../../../../features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';
import {setActiveTab} from '../../../../shared/store/menuSlice';
import ShopIcon from '../../../../shared/assets/icons/ShopIcon';
import {
  buttonSizes,
  buttonVariants,
} from '../../../../shared/constants/global';
import {NotificationMenuIcon} from '../../../../shared/assets/icons/NotificationMenuIcon';
import {getInitials} from '../../../../features/layout/screens/MainLayoutScreen/MainLayoutController';
import QuickAssistIcon from '../../../../shared/assets/icons/QuickAssist';
import ProfilePicture from '../../../../shared/components/Profile/Profile';
import ConfirmLogoutModal from '../../../../features/auth/screens/ConfirmLogoutModal';
import IndirectActivityModal from '../../../../shared/components/layout/Header/IndirectActivity/IndirectActivityModal/IndirectActivityModal';
import {useHeaderController} from './HeaderController';
import {showErrorToast, showErrorModal, resetOvertimeConfirm} from '../../../store/errorSlice';
import {definedBeginShiftErrorMessages} from '@features/repairDetails/constants/repairDetails.constant';
import ConfirmModal from '@shared/components/modals/ConfirmModal/ConfirmModal';
import {GlobalErrorModal} from '@shared/components/feedback';
import {useShiftStatusGuard} from '@shared/hooks/useShiftStatusGuard';
import AlertCircleIcon from '@shared/assets/icons/AlertCircleIcon';

// ---------------------------------------------------------------------------
// Module-level helpers for handleConfirmIndirectActivity — each checks one
// error condition and returns true (early return) when it applies.
// ---------------------------------------------------------------------------

function checkIndirectWoThreshold(
  data: any,
  dispatch: any,
  t: any,
  shiftStatusIndicator: any,
): boolean {
  if (
    !data?.isSuccess &&
    data?.message?.toLowerCase().includes(definedBeginShiftErrorMessages.woThreshold.toLowerCase())
  ) {
    dispatch(showErrorModal({
      title: t('repairDetails.spendingLimitTitle'),
      subTitle: t('repairDetails.spendingLimitMessage'),
      message: '',
      noTranslation: true,
      ctas: [
        {label: t('repairDetails.cancelAuthorizationCTA'), type: 'CANCEL_SPENDING_LIMIT', primary: false},
        {label: t('repairDetails.requestAuthorizationCTA'), type: 'REQUEST_AUTHORIZATION', payload: {documentId: shiftStatusIndicator}},
      ],
      screen: 'repairDetails',
    }));
    return true;
  }
  return false;
}

function checkIndirectLabourOvertime(
  data: any,
  dispatch: any,
  t: any,
  setPendingAuthType: (v: any) => void,
): boolean {
  if (
    !data?.isSuccess &&
    data?.message?.includes(definedBeginShiftErrorMessages.labourOvertime)
  ) {
    setPendingAuthType('indirectLabourOvertime');
    dispatch(showErrorModal({
      subTitle: data.message,
      title: t('createFlow.common.modalTitles.warning'),
      message: '',
      noTranslation: true,
      ctas: [{label: t('common.networkError.unableCtaDismiss'), type: 'YES'}],
      screen: 'repairCard',
    }));
    return true;
  }
  return false;
}

function checkIndirectOvertimeContinue(
  data: any,
  setIndirectOvertimeMessage: (v: string) => void,
  setShowIndirectOvertimeModal: (v: boolean) => void,
): boolean {
  if (
    !data?.isSuccess &&
    data?.message === definedBeginShiftErrorMessages.indirectActivityOvertimeContinue
  ) {
    setIndirectOvertimeMessage(data.message);
    setShowIndirectOvertimeModal(true);
    return true;
  }
  return false;
}

function checkIndirectEndShiftOvertime(
  data: any,
  dispatch: any,
  t: any,
  setPendingAuthType: (v: any) => void,
): boolean {
  if (
    !data?.isSuccess &&
    data?.message?.includes(definedBeginShiftErrorMessages.endShiftOverTime)
  ) {
    setPendingAuthType('endShiftOvertime');
    dispatch(showErrorModal({
      subTitle: data.message,
      title: t('createFlow.common.modalTitles.warning'),
      message: '',
      noTranslation: true,
      ctas: [{label: t('common.networkError.unableCtaDismiss'), type: 'YES'}],
      screen: 'repairCard',
    }));
    return true;
  }
  return false;
}

/**
 * @component Header
 * @description A versatile header component with configurable elements like language selection, logout, help, and search.
 * @param {HeaderProps} props - The props for configuring the header's visibility and behavior.
 */
const Header = (props: HeaderProps) => {
  const {
    showLanguageCTA = false,
    showLogoutCTA = false,
    showHelpCTA = false,
    showIndirectActivity = false,
    showSearchBar = false,
    activeTab = '',
    showSelectedShop = false,
    showNotificationIcon = false,
    showProfile = false,
    showShiftStatusIndicator = false,
    showQuickActions = false,
  } = props;

  // Hooks and state management
  const theme = useScreenTheme('header');
  const isNarrow = useIsZoomed();
  const styles = useMemo(
    () => createHeaderStyles(theme, isNarrow),
    [theme, isNarrow],
  );
  const {getLanguages, t, getSelectedLanguage} = useCommonController();
  const {getIndirectActivities, confirmIndirectActivity} =
    useHeaderController();
  const {checkShiftStatus} = useShiftStatusGuard();
  const [selectedLanguage, setSelectedLanguage] = useState<DropdownOption>();
  const [indirectActivities, setIndirectActivities] = useState<
    IndirectActivityResponse[]
  >([]);
  const dispatch = useDispatch();
  const {overtimeConfirmed} = useSelector((state: RootState) => state.error);
  const {colorMode, systemColorScheme} = useSelector((state: RootState) => state.theme);
  const isDark =
    colorMode === 'dark' ||
    (colorMode === 'system' && systemColorScheme === 'dark');
  const inputRef = useRef<ComponentRef<typeof TouchableOpacity>>(null);
  const {language} = useSelector((state: RootState) => state.language);
  const {selectedShop} = useSelector((state: RootState) => state.shop);
  const [searchValue, setSearchValue] = useState('');
  const {selectedTechnician, technicianThumbnail} = useSelector(
    (state: RootState) => state.technician,
  );
  const {shiftStarted, shiftStatusIndicator} = useSelector(
    (state: RootState) => state.shiftManagement,
  );
  const [showLogoutConfirmationModal, setShowLogoutConfirmationModal] =
    useState<boolean>(false);
  const [isIndirectActivityModalVisible, setIsIndirectActivityModalVisible] =
    useState(false);
  const [selectedIndirectActivity, setSelectedIndirectActivity] =
    useState<number>(0);
  const [showIndirectOvertimeModal, setShowIndirectOvertimeModal] =
    useState(false);
  const [indirectOvertimeMessage, setIndirectOvertimeMessage] = useState('');
  const [pendingAuthType, setPendingAuthType] = useState<
    'indirectLabourOvertime' | 'endShiftOvertime' | null
  >(null);
  const {userDetails} = useSelector((state: RootState) => state.user);
  const userTimeZone =
      (userDetails?.keyName as string | undefined) ??
      userDetails?.displayName ??
      '';
  const {showQuickAssistScreen} = useSelector(
    (state: RootState) => state.quickAssist,
  );

  useEffect(() => {
    if (overtimeConfirmed && pendingAuthType === 'indirectLabourOvertime') {
      dispatch(resetOvertimeConfirm());
      setPendingAuthType(null);
      // Retry with labour overtime check disabled; prompt check remains enabled
      handleConfirmIndirectActivity(selectedIndirectActivity, false, true);
    }
    if (overtimeConfirmed && pendingAuthType === 'endShiftOvertime') {
      dispatch(resetOvertimeConfirm());
      setPendingAuthType(null);
      // Retry with isIndirectOvertimeCheckRequired=false
      handleConfirmIndirectActivity(selectedIndirectActivity, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overtimeConfirmed]);

  // Prefetch indirect activities once on mount so the modal opens instantly
  useEffect(() => {
    const fetchIndirectActivities = async () => {
      const data = await getIndirectActivities();
      if (data) {
        setIndirectActivities(data);
      }
    };
    if (showIndirectActivity) {
      fetchIndirectActivities();
    }
  }, [showIndirectActivity]);

  // Effect to synchronize the selected language with the global state
  useEffect(() => {
    const syncLanguage = async () => {
      const results = await getSelectedLanguage(language);

      if (results && results.length > 0) {
        const matched = results[0];
        if (matched?.value == null) return;

        const newLangObj = {
          ...matched,
          label: String(matched.value),
          value: matched.value as string | number,
        };
        if (selectedLanguage?.value !== newLangObj.value) {
          setSelectedLanguage(newLangObj);
        }
      }
    };

    syncLanguage();
  }, [language, getSelectedLanguage, selectedLanguage?.value]);

  /**
   * Removes all flow-persistence keys from secureStorage so that re-login always
   * starts at the home screen on both iOS and Android.
   * On iOS the MainLayoutScreen beforeRemove guard also does this, but on Android
   * there is no beforeRemove event fired for navigation.reset(), so we must clear
   * here explicitly to avoid the work-order flow being restored after logout.
   */
  const clearFlowPersistence = () =>
    Promise.all([
      secureStorage.removeItem(WO_FLOW_PERSIST_STEP_KEY),
      secureStorage.removeItem(WO_FLOW_PERSIST_ASSET_KEY),
      secureStorage.removeItem(WO_FLOW_PERSIST_DOCUMENT_ID_KEY),
      secureStorage.removeItem(WO_FLOW_PERSIST_STEP1_KEY),
      secureStorage.removeItem(REPAIR_FLOW_PERSIST_STEP_KEY),
      secureStorage.removeItem(REPAIR_FLOW_PERSIST_WORK_ORDER_KEY),
    ]).catch(() => {});

  /**
   * @function logout
   * @description Signs out from Azure CIAM and navigates to the agency selection screen.
   * Clears oidc-client-ts session state and the stored auth token, then navigates.
   * The next signinRedirect/signinWithBrowser always carries prompt=login so Azure
   * CIAM will prompt for credentials regardless of any residual server-side session.
   */
  const logout = async () => {
    // Set the intentionalNavFlag BEFORE any async work so that the
    // beforeRemove guard on ShopHomePage (and MainLayoutScreen) cannot
    // re-open the logout modal while signoutWithBrowser() clears tokens.
    intentionalNavFlag.current = true;
    // Clear WO/Repair flow persistence so Android re-login lands on the
    // home screen. On iOS the MainLayoutScreen beforeRemove guard handles
    // this, but Android does not fire beforeRemove for navigation.reset().
    await clearFlowPersistence();
    if (showProfile) {
      const role = userIdStorage.getRole();
      dispatch(closeQuickAssistScreen());
      dispatch(clearSelectedTechnicianData());
      dispatch(setActiveTab(menuTabs.home));

      // if (role === 'Admin') {
      //   navigationRef.current?.reset({
      //     index: 0,
      //     routes: [{name: 'ShopHomePage' as never}],
      //   });
      // } else {
      //   userIdStorage.clear();
      //   await signoutWithBrowser();
      //   // Navigate first — this unmounts the screen and the modal together,
      //   // preventing the blink caused by closing the modal before navigation.
      //   navigationRef.current?.reset({
      //     index: 0,
      //     routes: [{name: 'AgencySelectionScreen' as never}],
      //   });
      // }
      navigationRef.current?.reset({
        index: 0,
        routes: [{name: 'ShopHomePage' as never}],
      });
    } else {
      dispatch(closeQuickAssistScreen());
      await signoutWithBrowser(); // clears oidc state + stored token on both platforms
      // Navigate first — this unmounts the screen and the modal together,
      // preventing the blink caused by closing the modal before navigation.
      navigationRef.current?.reset({
        index: 0,
        routes: [{name: 'AgencySelectionScreen' as never}],
      });
    }
    // NOTE: do NOT call setShowLogoutConfirmationModal(false) here.
    // The navigation reset above unmounts this component entirely, so the
    // modal disappears as part of unmount. Closing the modal explicitly
    // before navigation would briefly expose the ShopHomePage content and
    // cause a visible screen blink.
  };

  /**
   * @function indirectActivity
   * @description Opens the indirect activity modal using prefetched data.
   *  First checks the live shift status; if statusFlag === 2 (not in shift)
   *  the session-expired modal is shown instead and the activity modal is
   *  not opened.
   */
  const indirectActivity = async () => {
    const ok = await checkShiftStatus();
    if (!ok) return;
    setIsIndirectActivityModalVisible(true);
  };


  const handleIndirectActivityConfirm = (selectedActivity: number) => {
    setSelectedIndirectActivity(selectedActivity);
    handleConfirmIndirectActivity(selectedActivity);
  };

  const handleConfirmIndirectActivity = async (
    selectedActivity: number,
    isIndirectOvertimeCheckRequired = true,
    isPromptOvertimeCheckRequired = true,
  ) => {
    try {
      const data = await confirmIndirectActivity(
        selectedActivity,
        Number(selectedTechnician?.selectedTechnicianId),
        userTimeZone,
        isIndirectOvertimeCheckRequired,
        isPromptOvertimeCheckRequired,
      );
      if (!data) return;

      // Delegate each error condition to a module-level checker (returns true = handled).
      if (checkIndirectWoThreshold(data, dispatch, t, shiftStatusIndicator)) return;
      if (checkIndirectLabourOvertime(data, dispatch, t, setPendingAuthType)) return;
      if (checkIndirectOvertimeContinue(data, setIndirectOvertimeMessage, setShowIndirectOvertimeModal)) return;
      if (checkIndirectEndShiftOvertime(data, dispatch, t, setPendingAuthType)) return;

      dispatch(showErrorToast({toastMessage: data?.message, successToast: data?.isSuccess}));
      if (!data?.isSuccess) return;

      dispatch(closeQuickAssistScreen());
      dispatch(clearSelectedTechnicianData());
      dispatch(setActiveTab(menuTabs.home));
      intentionalNavFlag.current = true;
      await clearFlowPersistence();

      const role = userIdStorage.getRole();
      // if (role === 'Admin') {
      //   navigationRef.current?.reset({
      //     index: 0,
      //     routes: [{name: 'ShopHomePage' as never}],
      //   });
      // } else {
      //   userIdStorage.clear();
      //   await signoutWithBrowser();
      //   navigationRef.current?.reset({
      //     index: 0,
      //     routes: [{name: 'AgencySelectionScreen' as never}],
      //   });
      // }
      navigationRef.current?.reset({
        index: 0,
        routes: [{name: 'ShopHomePage' as never}],
      });
    } catch {
      // Unexpected network / server error — swallow silently.
    } finally {
      setIsIndirectActivityModalVisible(false);
    }
  };

  /**
   * @function quickAssist
   * @description Placeholder for handling quick assist actions.
   */
  const quickAssist = () => {
    // Placeholder for future implementation
  };

  return (
    // @ts-ignore — role="banner" is a web-only ARIA landmark prop supported by React Native Web
    <View style={styles.headerContainer} role="banner" nativeID="site-header">
      {/* Left section of the header */}
      <View style={styles.headerLeft}>
        <View
          style={styles.logo}
          testID="Logo"
          nativeID="header-logo"
          accessible={true}
          accessibilityRole="image"
          accessibilityLabel={t('header.accessibility.companyLogo')}
          focusable={true}
          tabIndex={0}>
          {isDark ? <FASTERLogoDark /> : <FASTERLogoLight />}
        </View>
        {showSelectedShop &&
          selectedShop?.name !== '' &&
          selectedShop?.name !== 'All Shops' &&
          selectedShop?.show && (
            <View style={styles.selectedShop} testID="AllShops">
              <ShopIcon />
              <Text style={styles.selectedShopText}>{selectedShop?.name}</Text>
            </View>
          )}
        {showSearchBar && activeTab === menuTabs.home && (
          <View
            testID="searchInputId"
            style={styles.searchInputContainer}
            accessible={false}>
            <SearchInput
              placeholder={t('header.searchInputPlaceholder')}
              onClick={(value: string) => setSearchValue(value)}
              searchText={searchValue}
              containerStyle={styles.containerStyle}
              showArrowIcon={false}
              onClearSearch={() => {}}
              accessibilityRole="search"
              accessibilityLabel={t('header.searchInputPlaceholder')}
              accessibilityHint={t('header.accessibility.VinDropdown')}
            />
          </View>
        )}
      </View>

      {/* Right section of the header */}
      <View style={styles.headerRight}>
        <View style={styles.buttonsContainer}>
          {showQuickActions && (
            <Button
              title={t('header.quickAssist')}
              onPress={() => quickAssist()}
              disabled={!shiftStarted}
              fullWidth={false}
              containerStyle={[
                styles.quickAssistContainer,
                !shiftStarted && styles.disabledView,
              ]}
              customTextStyle={styles.quickAssistText}
              showLeftIcon
              customLeftIcon={
                <QuickAssistIcon
                  color={(theme as any).headerIndirectActivityIconColor}
                />
              }
              customIconStyles={styles.buttonContainerTextStyles}
              size={buttonSizes.large}
              accessibilityRole="button"
              accessibilityLabel={t('header.quickAssist')}
              accessibilityHint={t('header.accessibility.quickAssist')}
              accessible={true}
              testID="QuickAssit"
            />
          )}
          {showIndirectActivity && (
            <Button
              title={t('header.indirectActivity')}
              onPress={() => indirectActivity()}
              fullWidth={false}
              containerStyle={[
                shiftStarted
                  ? styles.indirectActivityEnabledContainer
                  : styles.indirectActivityDisabledContainer,
              ]}
              customTextStyle={
                shiftStarted
                  ? styles.indirectEnabledText
                  : styles.indirectDisabledText
              }
              showLeftIcon
              disabled={!shiftStarted || showQuickAssistScreen}
              customLeftIcon={
                <IndirectActivityIcon
                  color={
                    shiftStarted
                      ? (theme as any).headerIndirectActivityIconColor
                      : (theme as any).headerIndirectActivityDisabledIconColor
                  }
                />
              }
              customIconStyles={styles.buttonContainerTextStyles}
              size={buttonSizes.large}
              variant={buttonVariants.secondary}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('header.indirectActivity')}
              accessibilityHint={t('header.accessibility.indirectActivity')}
              focusable={true}
              testID="IndirectActivity"
            />
          )}

          {showLogoutCTA && (
            <Button
              title={t('shopHomePage.logOut')}
              onPress={() => setShowLogoutConfirmationModal(true)}
              fullWidth={false}
              containerStyle={styles.logoutButtonContainer}
              customTextStyle={styles.logoutText}
              showLeftIcon
              customLeftIcon={
                <LogoutIcon color={(theme as any).shopHomeLogoutCTAIcon} />
              }
              customIconStyles={styles.buttonContainerTextStyles}
              size={buttonSizes.large}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('shopHomePage.logOut')}
              accessibilityHint={t('header.accessibility.logOut')}
              testID="ShopHomePageLogout"
              variant={buttonVariants.secondary}
            />
          )}
        </View>
        <View style={styles.blockContainer}>
          {showHelpCTA && (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={t('shopHomePage.help')}
                accessibilityHint={t('header.accessibility.help')}
                testID="helpButton"
                onPress={() => {
                    // Inside MainLayoutScreen (showProfile=true) → switch to the
                    // Help tab so the sidebar/bottom menu remains visible.
                    // On ShopHomePage / AgencySelectionScreen → push the stack screen.
                    if (showProfile) {
                      dispatch(setActiveTab(menuTabs.help));
                    } else {
                      navigate('HelpScreen');
                    }
                  }}>
                <View style={styles.helperIcon}>
                  <HelpIcon color={(theme as any).helpIcon} />
                  <Text style={styles.helpText}>{t('shopHomePage.help')}</Text>
                </View>
              </TouchableOpacity>
            )}
          {showNotificationIcon && (
            <TouchableOpacity
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('header.accessibility.notificationsButton')}
              accessibilityHint={t('header.accessibility.notifications')}
              testID="notificationButton">
              <NotificationMenuIcon hasNotifications={true} />
            </TouchableOpacity>
          )}
          {showLanguageCTA && (
            <View style={styles.languageContainer}>
              <Dropdown
                customIcon={<View />}
                  image={
                  <LanguageIcon
                    color={(theme as any).shopHomeLanguageIconColor}
                  />
                }
                selectedSort={selectedLanguage}
                options={getLanguages}
                onSelect={(option: DropdownOption) => {
                  setSelectedLanguage({
                    ...option,
                    label: String(option?.value),
                  });
                  dispatch(setLanguage(option?.value as string));
                  storageService.setLanguage(option.value as string);
                }}
                customTextStyles={styles.languageDropdown}
                customContainerStyles={styles.languageMainDropdown}
                inputRef={inputRef}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={(() => {
                  const selectedOption = getLanguages.find(
                    lang => lang.value === selectedLanguage?.value,
                  );
                  const selectedName = selectedOption?.label;
                  const baseLabel = t('header.accessibility.languageSelect');
                  if (!selectedName) {
                    return baseLabel;
                  }
                  return `${baseLabel}, ${selectedName}, ${t('header.accessibility.languageSelected')}`;
                })()}
                accessibilityHint={t('header.accessibility.languageSection')}
                accessibilityState={{selected: !!selectedLanguage?.value}}
                testID="languageDropdown"
              />
            </View>
          )}
          {<View style={styles.lineContainer} />}
          {showShiftStatusIndicator && (
            // @ts-ignore — tabIndex is a web-only prop not in RN types
            <View
              style={styles.shiftStatusIndicator}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`${t('dashboard.status')}${
                shiftStatusIndicator ?? ''
              }`}
              focusable={true}
              tabIndex={0}
              testID="shiftStatusIndicator">
              <Text
                style={styles.subTitle}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants"
                // @ts-ignore — aria-hidden is a web-only prop to prevent double-reading by screen readers
                aria-hidden={true}>
                {`${t('dashboard.status')}${shiftStatusIndicator ?? ''}`}
              </Text>
            </View>
          )}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={
              isDark
                ? t('header.switchToLight', 'Switch to light mode')
                : t('header.switchToDark', 'Switch to dark mode')
            }
            testID="darkModeToggleButton"
            onPress={() => {
              // Cycle: if currently forced (dark/light) → revert to 'system' so OS
              // dark mode works again; if in system mode → force the opposite.
              const nextMode =
                colorMode === 'system'
                  ? isDark ? 'light' : 'dark'
                  : 'system';
              dispatch(setColorMode(nextMode));
              storageService.setColorMode(nextMode);
            }}>
            {isDark
              ? <LightModeIcon color={(theme as any).helpIcon} size={22} />
              : <DarkModeIcon color={(theme as any).helpIcon} size={22} />}
          </TouchableOpacity>
          {showProfile && (
            <ProfilePicture
              testID="headerProfilePicture"
              name={getInitials(selectedTechnician?.selectedTechnicianName)}
              image={
                technicianThumbnail
                  ? {uri: `data:image/jpeg;base64,${technicianThumbnail}`}
                  : undefined
              }
              online={shiftStarted}
              fromHeader={true}
              onLogout={() => setShowLogoutConfirmationModal(true)}
            />
          )}
        </View>
        {showQuickAssistScreen ? (
          <ConfirmModal
            isVisible={showLogoutConfirmationModal}
            onClose={() => setShowLogoutConfirmationModal(false)}
            onConfirm={() => {
              setShowLogoutConfirmationModal(false);
              logout();
            }}
            title={t('mainLayout.quickAssistLogoutModal.title')}
            description={t('mainLayout.quickAssistLogoutModal.message')}
            changeCTA={t('mainLayout.quickAssistLogoutModal.cancelCTA')}
            confirmCTA={t('mainLayout.quickAssistLogoutModal.confirmCTA')}
            onChangeSelection={() => setShowLogoutConfirmationModal(false)}
            titleIcon={<AlertCircleIcon />}
            showClose={true}
          />
        ) : (
          <ConfirmLogoutModal
            isVisible={showLogoutConfirmationModal}
            onClose={() => setShowLogoutConfirmationModal(false)}
            onLogOut={() => logout()}
          />
        )}
      </View>
      {isIndirectActivityModalVisible && (
        <IndirectActivityModal
          isVisible={isIndirectActivityModalVisible}
          onClose={() => setIsIndirectActivityModalVisible(false)}
          onConfirm={handleIndirectActivityConfirm}
          indirectActivityOptions={indirectActivities}
        />
      )}
      {showIndirectOvertimeModal && (
        <ConfirmModal
          isVisible={showIndirectOvertimeModal}
          onClose={() => setShowIndirectOvertimeModal(false)}
          onConfirm={() => {
            setShowIndirectOvertimeModal(false);
            handleConfirmIndirectActivity(selectedIndirectActivity, false, false);
          }}
          title={t('createFlow.common.modalTitles.warning')}
          description={indirectOvertimeMessage}
          confirmCTA={t('common.confirmModal.yes')}
          changeCTA={t('common.confirmModal.no')}
          onChangeSelection={() => setShowIndirectOvertimeModal(false)}
          showClose={true}
        />
      )}
      {Platform.OS !== 'android' && <GlobalErrorModal />}
    </View>
  );
};

export default Header;
